
import React, { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

interface ReportData {
  orders: any[];
  loading: boolean;
  error: string;
}

export default function ReportsSection() {
  const [reportData, setReportData] = useState<ReportData>({
    orders: [],
    loading: true,
    error: ''
  });
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [activeTab, setActiveTab] = useState<'overview' | 'sales' | 'menu' | 'customers'>('overview');

  const fetchReportData = async () => {
    try {
      setReportData(prev => ({ ...prev, loading: true, error: '' }));
      
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        console.log('Supabase tidak terkonfigurasi, menggunakan data kosong');
        setReportData({
          orders: [],
          loading: false,
          error: ''
        });
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/get-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn(`HTTP ${response.status}: ${response.statusText}`);
        setReportData({
          orders: [],
          loading: false,
          error: ''
        });
        return;
      }

      const result = await response.json();
      
      if (result.success) {
        setReportData({
          orders: result.orders || [],
          loading: false,
          error: ''
        });
      } else {
        console.warn('Gagal mengambil data:', result.error);
        setReportData({
          orders: [],
          loading: false,
          error: ''
        });
      }
    } catch (error: any) {
      console.warn('Error mengambil data laporan:', error);
      setReportData({
        orders: [],
        loading: false,
        error: ''
      });
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  // Filter orders berdasarkan tanggal
  const filteredOrders = reportData.orders.filter(order => {
    try {
      const orderDate = new Date(order.created_at).toISOString().split('T')[0];
      return orderDate >= dateRange.start && orderDate <= dateRange.end;
    } catch {
      return false;
    }
  });

  // Analisis data dengan handling untuk kondisi kosong
  const salesAnalysis = React.useMemo(() => {
    const paidOrders = filteredOrders.filter(order => order.payment_status === 'paid');
    const totalRevenue = paidOrders.reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0);
    const totalOrders = filteredOrders.length;
    const avgOrderValue = totalOrders > 0 ? totalRevenue / paidOrders.length : 0;

    // Analisis per hari
    const dailySales = {};
    paidOrders.forEach(order => {
      try {
        const date = new Date(order.created_at).toISOString().split('T')[0];
        if (!dailySales[date]) {
          dailySales[date] = { revenue: 0, orders: 0 };
        }
        dailySales[date].revenue += Number(order.total_amount) || 0;
        dailySales[date].orders += 1;
      } catch {
        // Skip invalid dates
      }
    });

    // Menu terlaris
    const menuStats = {};
    filteredOrders.forEach(order => {
      try {
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach(item => {
            if (item && item.name) {
              if (!menuStats[item.name]) {
                menuStats[item.name] = { 
                  name: item.name, 
                  sold: 0, 
                  revenue: 0,
                  price: Number(item.price) || 0
                };
              }
              menuStats[item.name].sold += Number(item.quantity) || 0;
              menuStats[item.name].revenue += (Number(item.price) || 0) * (Number(item.quantity) || 0);
            }
          });
        }
      } catch {
        // Skip invalid items
      }
    });

    const topMenus = Object.values(menuStats)
      .sort((a: any, b: any) => b.sold - a.sold)
      .slice(0, 10);

    return {
      totalRevenue,
      totalOrders,
      paidOrders: paidOrders.length,
      avgOrderValue,
      dailySales,
      topMenus
    };
  }, [filteredOrders]);

  const exportData = (format: 'excel' | 'pdf') => {
    if (filteredOrders.length === 0) {
      alert('❌ Tidak ada data untuk diekspor. Silakan pilih periode yang memiliki data transaksi.');
      return;
    }

    // Prepare export data
    const exportOrders = filteredOrders.map(order => ({
      'No. Order': order.order_number,
      'Tanggal': new Date(order.created_at).toLocaleDateString('id-ID'),
      'Waktu': new Date(order.created_at).toLocaleTimeString('id-ID'),
      'Customer': order.customer_name,
      'Meja': order.table_number || 'N/A',
      'Items': order.items?.map(item => `${item.quantity}x ${item.name}`).join(', ') || '',
      'Total': Number(order.total_amount) || 0,
      'Status': order.status,
      'Pembayaran': order.payment_status,
      'Metode Bayar': order.payment_method || 'N/A'
    }));

    if (format === 'excel') {
      // Create CSV content
      const headers = Object.keys(exportOrders[0]);
      const csvContent = [
        headers.join(','),
        ...exportOrders.map(row => 
          headers.map(header => 
            typeof row[header] === 'string' && row[header].includes(',') 
              ? `"${row[header]}"` 
              : row[header]
          ).join(',')
        )
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `laporan-penjualan-${dateRange.start}-${dateRange.end}.csv`;
      link.click();

      alert('✅ Data berhasil diekspor ke format Excel (CSV)!');
    } else {
      // Generate PDF content (simplified)
      const pdfContent = `
LAPORAN PENJUALAN TAKEYA RESTAURANT
Periode: ${new Date(dateRange.start).toLocaleDateString('id-ID')} - ${new Date(dateRange.end).toLocaleDateString('id-ID')}

RINGKASAN:
- Total Pendapatan: Rp ${salesAnalysis.totalRevenue.toLocaleString('id-ID')}
- Total Pesanan: ${salesAnalysis.totalOrders}
- Pesanan Terbayar: ${salesAnalysis.paidOrders}
- Rata-rata per Pesanan: Rp ${salesAnalysis.avgOrderValue.toLocaleString('id-ID')}

DETAIL PESANAN:
${exportOrders.map(order => 
  `${order['No. Order']} | ${order['Tanggal']} | ${order['Customer']} | Rp ${Number(order['Total']).toLocaleString('id-ID')}`
).join('\n')}

Generated at: ${new Date().toLocaleString('id-ID')}
      `;

      // Create and download text file (as PDF alternative)
      const blob = new Blob([pdfContent], { type: 'text/plain;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `laporan-penjualan-${dateRange.start}-${dateRange.end}.txt`;
      link.click();

      alert('✅ Data berhasil diekspor dalam format teks!');
    }
  };

  if (reportData.loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center space-x-2 text-white">
          <i className="ri-loader-4-line text-2xl animate-spin w-6 h-6 flex items-center justify-center"></i>
          <span>Memuat data laporan...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Laporan Penjualan</h2>
          <p className="text-gray-300">Analisis mendalam performa bisnis restoran</p>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReportData}
            className="whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line mr-2 w-4 h-4 flex items-center justify-center"></i>
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-red-500/20 p-6">
        <h3 className="text-xl font-bold text-white mb-4">Filter Periode</h3>
        <div className="grid md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-gray-300 text-sm font-semibold mb-2">Tanggal Mulai</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-semibold mb-2">Tanggal Selesai</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <Button
              variant="primary"
              onClick={() => setDateRange({
                start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0]
              })}
              className="whitespace-nowrap cursor-pointer"
            >
              7 Hari Terakhir
            </Button>
          </div>
          <div>
            <Button
              variant="outline"
              onClick={() => setDateRange({
                start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0]
              })}
              className="whitespace-nowrap cursor-pointer"
            >
              Bulan Ini
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-800/50 p-1 rounded-2xl">
        {[
          { id: 'overview', name: 'Ringkasan', icon: 'ri-dashboard-line' },
          { id: 'sales', name: 'Penjualan', icon: 'ri-money-dollar-circle-line' },
          { id: 'menu', name: 'Menu', icon: 'ri-restaurant-line' },
          { id: 'customers', name: 'Pelanggan', icon: 'ri-user-line' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              activeTab === tab.id
                ? 'bg-red-600 text-white shadow-lg'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            <i className={`${tab.icon} w-5 h-5 flex items-center justify-center`}></i>
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Key Metrics */}
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-red-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-6">Metrik Utama</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <i className="ri-money-dollar-circle-line text-white text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <div className="text-2xl font-bold text-white">
                  Rp {salesAnalysis.totalRevenue.toLocaleString('id-ID')}
                </div>
                <div className="text-gray-400 text-sm">Total Pendapatan</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shopping-bag-line text-white text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <div className="text-2xl font-bold text-white">{salesAnalysis.totalOrders}</div>
                <div className="text-gray-400 text-sm">Total Pesanan</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-line text-white text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <div className="text-2xl font-bold text-white">{salesAnalysis.paidOrders}</div>
                <div className="text-gray-400 text-sm">Pesanan Terbayar</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <i className="ri-calculator-line text-white text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <div className="text-2xl font-bold text-white">
                  Rp {salesAnalysis.avgOrderValue.toLocaleString('id-ID')}
                </div>
                <div className="text-gray-400 text-sm">Rata-rata per Pesanan</div>
              </div>
            </div>
          </div>

          {/* Export Actions */}
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-red-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-6">Export Data</h3>
            <div className="space-y-4">
              <Button
                variant="outline"
                onClick={() => exportData('excel')}
                disabled={filteredOrders.length === 0}
                className="w-full whitespace-nowrap cursor-pointer border-green-500 text-green-400 hover:bg-green-500 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-file-excel-line mr-3 w-5 h-5 flex items-center justify-center"></i>
                Export ke Excel (CSV)
              </Button>
              <Button
                variant="outline"
                onClick={() => exportData('pdf')}
                disabled={filteredOrders.length === 0}
                className="w-full whitespace-nowrap cursor-pointer border-red-500 text-red-400 hover:bg-red-500 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-file-pdf-line mr-3 w-5 h-5 flex items-center justify-center"></i>
                Export ke PDF (Text)
              </Button>
              <div className="text-center mt-4">
                <p className="text-gray-400 text-sm">
                  {filteredOrders.length > 0 
                    ? `${filteredOrders.length} data siap untuk diekspor`
                    : 'Tidak ada data untuk periode ini'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Tab */}
      {activeTab === 'sales' && (
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-red-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6">Analisis Penjualan Harian</h3>
          {Object.keys(salesAnalysis.dailySales).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(salesAnalysis.dailySales)
                .sort(([a], [b]) => b.localeCompare(a))
                .map(([date, data]: [string, any]) => (
                <div key={date} className="flex items-center justify-between p-4 bg-gray-900/30 rounded-xl">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
                      <i className="ri-calendar-line text-white w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold">
                        {new Date(date).toLocaleDateString('id-ID', { 
                          weekday: 'long', 
                          day: '2-digit', 
                          month: 'long' 
                        })}
                      </div>
                      <div className="text-gray-400 text-sm">{data.orders} pesanan</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-green-400">
                      Rp {data.revenue.toLocaleString('id-ID')}
                    </div>
                    <div className="text-gray-400 text-sm">
                      Rp {Math.round(data.revenue / data.orders).toLocaleString('id-ID')}/pesanan
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-line-chart-line text-gray-400 text-3xl w-10 h-10 flex items-center justify-center"></i>
              </div>
              <h4 className="text-white font-semibold mb-2">Belum Ada Data Penjualan</h4>
              <p className="text-gray-400 mb-4">Data penjualan harian akan muncul setelah ada transaksi</p>
              <div className="text-sm text-gray-500">
                Pilih periode yang berbeda atau tunggu transaksi baru masuk
              </div>
            </div>
          )}
        </div>
      )}

      {/* Menu Tab */}
      {activeTab === 'menu' && (
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-red-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6">Performa Menu</h3>
          {salesAnalysis.topMenus.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {salesAnalysis.topMenus.map((menu: any, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 bg-gray-900/30 rounded-xl">
                  <div className="w-12 h-12 bg-yellow-600 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold">#{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">{menu.name}</div>
                    <div className="text-gray-400 text-sm">
                      {menu.sold} terjual • Rp {menu.revenue.toLocaleString('id-ID')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-red-400 font-bold">
                      Rp {menu.price.toLocaleString('id-ID')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-restaurant-line text-gray-400 text-3xl w-10 h-10 flex items-center justify-center"></i>
              </div>
              <h4 className="text-white font-semibold mb-2">Belum Ada Data Menu</h4>
              <p className="text-gray-400 mb-4">Data performa menu akan muncul setelah ada penjualan</p>
              <div className="text-sm text-gray-500">
                Statistik menu terlaris berdasarkan jumlah terjual dan pendapatan
              </div>
            </div>
          )}
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-red-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-6">Analisis Pelanggan</h3>
          
          {filteredOrders.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-8">
              {/* Customer Stats */}
              <div className="space-y-6">
                <div className="text-center p-6 bg-gray-900/30 rounded-xl">
                  <div className="text-3xl font-bold text-blue-400 mb-2">
                    {new Set(filteredOrders.map(o => o.customer_phone || o.customer_name).filter(id => id)).size}
                  </div>
                  <div className="text-gray-400">Pelanggan Unik</div>
                </div>
                
                <div className="text-center p-6 bg-gray-900/30 rounded-xl">
                  <div className="text-3xl font-bold text-green-400 mb-2">
                    {Math.round(filteredOrders.length / Math.max(new Set(filteredOrders.map(o => o.customer_phone || o.customer_name).filter(id => id)).size, 1) * 10) / 10}
                  </div>
                  <div className="text-gray-400">Rata-rata Pesanan per Pelanggan</div>
                </div>
              </div>

              {/* Recent Customers */}
              <div>
                <h4 className="text-white font-semibold mb-4">Pelanggan Terbaru</h4>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {filteredOrders
                    .filter(order => order.customer_name && order.customer_name !== 'Guest')
                    .slice(0, 10)
                    .map((order, index) => (
                    <div key={`${order.id}-${index}`} className="flex items-center justify-between p-3 bg-gray-900/30 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                          <i className="ri-user-line text-white w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <div className="text-white font-semibold">{order.customer_name}</div>
                          {order.customer_phone && (
                            <div className="text-gray-400 text-sm">{order.customer_phone}</div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-green-400 font-semibold">
                          Rp {Number(order.total_amount).toLocaleString('id-ID')}
                        </div>
                        <div className="text-gray-400 text-xs">
                          {new Date(order.created_at).toLocaleDateString('id-ID')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-line text-gray-400 text-3xl w-10 h-10 flex items-center justify-center"></i>
              </div>
              <h4 className="text-white font-semibold mb-2">Belum Ada Data Pelanggan</h4>
              <p className="text-gray-400 mb-4">Data analisis pelanggan akan muncul setelah ada transaksi</p>
              <div className="text-sm text-gray-500">
                Informasi tentang pelanggan unik, frekuensi pembelian, dan pola konsumsi
              </div>
            </div>
          )}
        </div>
      )}

      {/* Data Status Footer */}
      <div className="text-center py-4">
        <p className="text-gray-400 text-sm">
          {filteredOrders.length > 0 
            ? `Menampilkan ${filteredOrders.length} transaksi dari periode ${new Date(dateRange.start).toLocaleDateString('id-ID')} - ${new Date(dateRange.end).toLocaleDateString('id-ID')}`
            : 'Tidak ada data untuk periode yang dipilih. Coba ubah filter tanggal atau tunggu transaksi baru.'
          }
        </p>
        <p className="text-gray-500 text-xs mt-1">
          Data diperbarui secara real-time • Terakhir refresh: {new Date().toLocaleTimeString('id-ID')}
        </p>
      </div>
    </div>
  );
}
